import { appliesWhenValues, initCriterion, itemScope } from '@/common/components/ConditionsEditor/constants'
import {
  transformConditionsFromBackend,
  transformConditionsToBackend,
} from '@/common/components/ConditionsEditor/helpers'
import { toStoreDateString } from '@/common/helpers/dates'
import { bonusAppliesToValues } from '@/configuration/pages/ConfigurationPage/ReturnPolicy/PolicyRulesConfiguration/PolicyRuleDetailConfiguration/PolicyRuleActionEditor/ActionConfigs/ApplyBonusConfig/constants'
import { isFixedDiscount } from '@/configuration/pages/ConfigurationPage/ReturnPolicy/PolicyRulesConfiguration/PolicyRuleDetailConfiguration/PolicyRuleActionEditor/ActionConfigs/ApplyBonusConfig/helpers'
import { feeApplicationModeValues } from '@/configuration/pages/ConfigurationPage/ReturnPolicy/PolicyRulesConfiguration/PolicyRuleDetailConfiguration/PolicyRuleActionEditor/ActionConfigs/ApplyExtraFeeConfig/constants'
import { triggerModeValues } from '@/configuration/pages/ConfigurationPage/ReturnPolicy/PolicyRulesConfiguration/PolicyRuleDetailConfiguration/PolicyRuleActionEditor/ActionConfigs/constants'
import { productDialogPlacements } from '@/configuration/pages/ConfigurationPage/ReturnPolicy/PolicyRulesConfiguration/PolicyRuleDetailConfiguration/PolicyRuleActionEditor/ActionConfigs/DisplayMessageConfig/constants'
import {
  actionTypeValues,
  backendActionTypes,
} from '@/configuration/pages/ConfigurationPage/ReturnPolicy/PolicyRulesConfiguration/PolicyRuleDetailConfiguration/PolicyRuleActionEditor/constants'
import { emptyCustomMessage } from '@/configuration/pages/ConfigurationPage/ReturnPolicy/PolicyRulesConfiguration/PolicyRuleDetailConfiguration/PolicyRuleActionEditor/CustomMessage/constants'
import { itemContexts } from '@/configuration/pages/ConfigurationPage/ReturnPolicy/PolicyRulesConfiguration/PolicyRuleDetailConfiguration/PolicyRuleConditionsEditor/constants'

export const formatToTwoDecimals = value => (value ? parseFloat(value).toFixed(2) : '')

const booleanFields = ['has_discounts', 'has_upsells', 'return_requested', 'already_returned', 'already_refunded']

const parseRefundMethodsAction = ({ refund_method_operation, refund_methods }) => {
  if (refund_method_operation === 'force') {
    return {
      action_type: actionTypeValues.forceRefundMethod,
      action_config: {
        refund_method: refund_methods?.[0]?.method || '',
        ignore_extra: refund_methods?.[0]?.ignore_extra ?? true,
      },
    }
  }

  return {
    action_type: actionTypeValues.disableRefundMethods,
    action_config: { refund_methods: refund_methods || [] },
  }
}

const parseExtraFeeAction = action => {
  const isPerItem = action.action_type === backendActionTypes.applyFeeItem

  return {
    action_type: actionTypeValues.applyExtraFee,
    action_config: {
      fee_concept: action.fee_concept || '',
      fee_amount: formatToTwoDecimals(action.fee_amount),
      fee_currency: action.fee_currency || '',
      fee_application_mode: isPerItem
        ? feeApplicationModeValues.perMatchingProduct
        : feeApplicationModeValues.perRequest,
      custom_message_enabled: action.custom_message_enabled ?? false,
      custom_message: action.custom_message,
      custom_message_type: action.custom_message_type || null,
    },
  }
}

const parseApplyBonusAction = action => {
  return {
    action_type: action.action_type,
    action_config: {
      bonus_free_return: action.bonus_free_return ?? false,
      bonus_free_exchange: action.bonus_free_exchange ?? false,
      bonus_refund_shipping: action.bonus_refund_shipping ?? false,
      bonus_exchange_discount: !!action.bonus_exchange_discount_value,
      bonus_exchange_discount_type: action.bonus_exchange_discount_type ?? '',
      bonus_exchange_discount_value: formatToTwoDecimals(action.bonus_exchange_discount_value),
      bonus_exchange_discount_currency: action.bonus_exchange_discount_currency ?? null,
      bonus_exchange_discount_label: action.bonus_exchange_discount_label ?? '',
      bonus_exchange_discount_applies_to:
        action.bonus_exchange_discount_applies_to ?? bonusAppliesToValues.exchangeOrder,
    },
  }
}

const parseDisplayMessageAction = action => {
  return {
    action_type: action.action_type,
    action_config: {
      custom_message: action.custom_message ?? emptyCustomMessage,
      custom_message_type: action.custom_message_type || null,
      display_message_placement: action.display_message_placement || '',
    },
  }
}

const parseDisplayConsentModalAction = action => {
  return {
    action_type: action.action_type,
    action_config: {
      consent_title: action.consent_title ?? emptyCustomMessage,
      consent_description: action.consent_description ?? emptyCustomMessage,
      consent_checkbox_text: action.consent_checkbox_text ?? emptyCustomMessage,
      display_message_placement: action.display_message_placement || '',
    },
  }
}

const parseForceRefundTriggerAction = action => {
  return {
    action_type: action.action_type,
    action_config: {
      refund_trigger_status: action.refund_trigger_status,
      refund_trigger_mode: action.refund_trigger_delay_days
        ? triggerModeValues.delayByDays
        : triggerModeValues.atTrigger,
      refund_trigger_delay_days: action.refund_trigger_delay_days || null,
      refund_trigger_delay_reason: action.refund_trigger_delay_reason || '',
    },
  }
}

const parseForceExchangeOrderTriggerAction = action => {
  return {
    action_type: action.action_type,
    action_config: {
      exchange_trigger_status: action.exchange_trigger_status,
      exchange_trigger_mode: action.exchange_trigger_delay_days
        ? triggerModeValues.delayByDays
        : triggerModeValues.atTrigger,
      exchange_trigger_delay_days: action.exchange_trigger_delay_days || null,
      exchange_trigger_delay_reason: action.exchange_trigger_delay_reason || '',
    },
  }
}

const actionParsers = {
  [backendActionTypes.forceDisableRefundMethods]: parseRefundMethodsAction,
  [backendActionTypes.applyFeeRequest]: parseExtraFeeAction,
  [backendActionTypes.applyFeeItem]: parseExtraFeeAction,
  [actionTypeValues.applyBonus]: parseApplyBonusAction,
  [actionTypeValues.displayMessage]: parseDisplayMessageAction,
  [actionTypeValues.displayConsentModal]: parseDisplayConsentModalAction,
  [actionTypeValues.forceRefundTrigger]: parseForceRefundTriggerAction,
  [actionTypeValues.forceExchangeOrderTrigger]: parseForceExchangeOrderTriggerAction,
}

const parseActionFromBackend = action => {
  if (!action) return { action_type: '', action_config: {} }

  const parseAction = actionParsers[action.action_type]

  if (parseAction) return parseAction(action)

  const { action_type, ...configFields } = action

  return { action_type, action_config: configFields }
}

const getActionConfig = (conditions, actionConfig) => {
  const actionCondition = conditions.find(condition => condition.applies_to_action)

  return {
    ...actionConfig,
    product_criteria: actionCondition ? actionCondition.criteria : [initCriterion],
  }
}

export const mapRuleToState = (rule, timeZone) => {
  const { action_type, action_config } = parseActionFromBackend(rule.action)
  const allConditions = transformConditionsFromBackend(rule.conditions, { booleanFields })
  const actionConfig = getActionConfig(allConditions, action_config)

  const remainingConditions = allConditions?.filter(condition => !condition.applies_to_action)

  return {
    id: rule.id,
    name: rule.name || '',
    notes: rule.notes || '',
    valid_from: toStoreDateString(rule.valid_from, timeZone) || '',
    valid_until: toStoreDateString(rule.valid_until, timeZone) || '',
    action_type,
    action_config: actionConfig,
    conditions: remainingConditions,
  }
}

const formatDateValue = value => {
  if (!value) return null
  if (typeof value === 'string') return value
  if (value.format) return value.format('YYYY-MM-DD')
  return value
}

const buildForceRefundMethodPayload = actionConfig => {
  return {
    action_type: backendActionTypes.forceDisableRefundMethods,
    refund_method_operation: 'force',
    refund_methods: actionConfig.refund_method
      ? [{ method: actionConfig.refund_method, ignore_extra: actionConfig.ignore_extra }]
      : [],
  }
}

const buildDisableRefundMethodsPayload = actionConfig => {
  return {
    action_type: backendActionTypes.forceDisableRefundMethods,
    refund_method_operation: 'disable',
    refund_methods: actionConfig.refund_methods,
  }
}

const buildApplyExtraFeePayload = actionConfig => {
  const isPerItem = actionConfig.fee_application_mode === feeApplicationModeValues.perMatchingProduct

  return {
    action_type: isPerItem ? backendActionTypes.applyFeeItem : backendActionTypes.applyFeeRequest,
    fee_concept: actionConfig.fee_concept,
    fee_amount: actionConfig.fee_amount,
    fee_currency: actionConfig.fee_currency,
    custom_message_enabled: actionConfig.custom_message_enabled,
    custom_message: actionConfig.custom_message,
    custom_message_type: actionConfig.custom_message_type,
  }
}

const buildApplyBonusPayload = (actionConfig, defaultCurrency) => {
  const bonusPayload = {
    action_type: actionTypeValues.applyBonus,
    bonus_free_return: actionConfig.bonus_free_return,
    bonus_free_exchange: actionConfig.bonus_free_exchange,
    bonus_refund_shipping: actionConfig.bonus_refund_shipping,
  }

  if (actionConfig.bonus_exchange_discount) {
    bonusPayload.bonus_exchange_discount_type = actionConfig.bonus_exchange_discount_type
    bonusPayload.bonus_exchange_discount_value = actionConfig.bonus_exchange_discount_value
    bonusPayload.bonus_exchange_discount_label = actionConfig.bonus_exchange_discount_label
    bonusPayload.bonus_exchange_discount_applies_to = actionConfig.bonus_exchange_discount_applies_to
  }

  if (isFixedDiscount(actionConfig)) {
    bonusPayload.bonus_exchange_discount_currency = actionConfig.bonus_exchange_discount_currency || defaultCurrency
  }

  return bonusPayload
}

const buildDisplayMessagePayload = actionConfig => {
  return {
    action_type: actionTypeValues.displayMessage,
    custom_message: actionConfig.custom_message,
    custom_message_type: actionConfig.custom_message_type,
    display_message_placement: actionConfig.display_message_placement,
  }
}

const buildDisplayConsentModalPayload = actionConfig => {
  return {
    action_type: actionTypeValues.displayConsentModal,
    consent_title: actionConfig.consent_title,
    consent_description: actionConfig.consent_description,
    consent_checkbox_text: actionConfig.consent_checkbox_text,
    display_message_placement: actionConfig.display_message_placement,
  }
}

const buildForceRefundTriggerPayload = actionConfig => {
  const delaysTrigger = actionConfig.refund_trigger_mode === triggerModeValues.delayByDays

  return {
    action_type: actionTypeValues.forceRefundTrigger,
    refund_trigger_status: actionConfig.refund_trigger_status,
    refund_trigger_delay_days: delaysTrigger ? Number(actionConfig.refund_trigger_delay_days) : null,
    refund_trigger_delay_reason: delaysTrigger ? actionConfig.refund_trigger_delay_reason || null : null,
  }
}

const buildForceExchangeOrderTriggerPayload = actionConfig => {
  const delaysTrigger = actionConfig.exchange_trigger_mode === triggerModeValues.delayByDays

  return {
    action_type: actionTypeValues.forceExchangeOrderTrigger,
    exchange_trigger_status: actionConfig.exchange_trigger_status,
    exchange_trigger_delay_days: delaysTrigger ? Number(actionConfig.exchange_trigger_delay_days) : null,
    exchange_trigger_delay_reason: delaysTrigger ? actionConfig.exchange_trigger_delay_reason || null : null,
  }
}

const actionPayloadBuilders = {
  [actionTypeValues.forceRefundMethod]: buildForceRefundMethodPayload,
  [actionTypeValues.disableRefundMethods]: buildDisableRefundMethodsPayload,
  [actionTypeValues.applyExtraFee]: buildApplyExtraFeePayload,
  [actionTypeValues.applyBonus]: buildApplyBonusPayload,
  [actionTypeValues.displayMessage]: buildDisplayMessagePayload,
  [actionTypeValues.displayConsentModal]: buildDisplayConsentModalPayload,
  [actionTypeValues.forceRefundTrigger]: buildForceRefundTriggerPayload,
  [actionTypeValues.forceExchangeOrderTrigger]: buildForceExchangeOrderTriggerPayload,
}

const buildActionPayload = (policyRule, defaultCurrency) => {
  const { action_type } = policyRule
  const { product_criteria: _productCriteria, ...actionConfig } = policyRule.action_config
  const buildPayload = actionPayloadBuilders[action_type]

  if (buildPayload) return buildPayload(actionConfig, defaultCurrency)

  if (actionConfig.extend_until_date !== undefined) {
    actionConfig.extend_until_date = formatDateValue(actionConfig.extend_until_date)
  }

  return {
    action_type,
    ...actionConfig,
  }
}

const hasProductCriteria = actionConfig => actionConfig.product_criteria.some(criterion => criterion.field)

const buildProductCondition = (actionConfig, itemContext) => {
  return {
    scope: itemScope,
    item_context: itemContext,
    applies_when: appliesWhenValues.atLeastOne,
    applies_to_action: true,
    criteria: actionConfig.product_criteria,
  }
}

const buildConditionsPayload = (policyRule, defaultCurrency) => {
  const conditions = [...(policyRule.conditions || [])]
  const { action_type, action_config } = policyRule

  if (action_type === actionTypeValues.blockItemOptions && hasProductCriteria(action_config)) {
    conditions.push(buildProductCondition(action_config, itemContexts.inOrder))
  }

  if (
    action_type === actionTypeValues.applyExtraFee &&
    action_config.fee_application_mode === feeApplicationModeValues.perMatchingProduct &&
    hasProductCriteria(action_config)
  ) {
    conditions.push(buildProductCondition(action_config, itemContexts.toBeReturned))
  }

  if (
    action_type === actionTypeValues.applyBonus &&
    action_config.bonus_exchange_discount_applies_to === bonusAppliesToValues.newRequestedProducts &&
    hasProductCriteria(action_config)
  ) {
    conditions.push(buildProductCondition(action_config, itemContexts.toBeReturned))
  }

  if (
    action_type === actionTypeValues.displayMessage &&
    productDialogPlacements.includes(action_config.display_message_placement) &&
    hasProductCriteria(action_config)
  ) {
    conditions.push(buildProductCondition(action_config, itemContexts.toBeReturned))
  }

  return transformConditionsToBackend(conditions, { defaultCurrency })
}

export const getPolicyRulePayload = (policyRule, defaultCurrency) => {
  const action = buildActionPayload(policyRule, defaultCurrency)
  const conditions = buildConditionsPayload(policyRule, defaultCurrency)

  const payload = {
    name: policyRule.name,
    notes: policyRule.notes || '',
    valid_from: formatDateValue(policyRule.valid_from),
    valid_until: formatDateValue(policyRule.valid_until),
    action,
    conditions,
  }

  if (policyRule.id && policyRule.id !== 'new') {
    payload.id = policyRule.id
  }

  return payload
}
